<script setup>
/**
 * Where a member answers a restaurant that would like to show their highlight
 * on its RM page — the page the ask email links to (Kapil, 2026-09-23: "for
 * now let's do via email first to ensure they are ok with it").
 *
 * The email's Yes / No links only open this page (the `answer` in the link
 * picks which button leads); nothing is decided until the member taps here.
 * Mail scanners open links, and a yes has to be theirs. A yes puts exactly
 * the words the restaurant saw on its page — nothing new to approve.
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import axios from '@axios'

const route = useRoute()

const requests = ref([])
const loading = ref(true)
const error = ref('')
const answered = ref({}) // shareId → 'yes' | 'no'
const saving = ref(null)

// The request the email was about leads; any others follow.
const ordered = computed(() => {
  const first = String(route.query.share || '')

  return [...requests.value].sort((a, b) => (b.shareId === first) - (a.shareId === first))
})

const suggested = computed(() => (route.query.answer === 'no' ? 'no' : 'yes'))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await axios.get('/profile/highlight-requests')

    requests.value = Array.isArray(data) ? data : []
  } catch {
    error.value = 'Could not load this right now. Please try again.'
  } finally {
    loading.value = false
  }
}

async function answer(req, yes) {
  saving.value = req.shareId
  error.value = ''
  try {
    await axios.post(`/profile/highlight-requests/${req.shareId}`, { approve: yes })
    answered.value = { ...answered.value, [req.shareId]: yes ? 'yes' : 'no' }
  } catch (e) {
    error.value = e?.response?.data?.message || 'Could not save your answer. Please try again.'
  } finally {
    saving.value = null
  }
}

onMounted(load)
</script>

<template>
  <div class="highlight-requests">
    <div
      v-if="loading"
      class="highlight-requests__skeleton"
    >
      <div class="highlight-requests__skeleton-line" />
      <div class="highlight-requests__skeleton-line short" />
    </div>

    <template v-else>
      <section
        v-for="req in ordered"
        :key="req.shareId"
        class="highlight-requests__card"
      >
        <h1 class="highlight-requests__title">
          {{ req.restaurant || 'A restaurant' }} would like to feature your highlight
        </h1>
        <p class="highlight-requests__words">
          "{{ req.text }}"
        </p>

        <template v-if="!answered[req.shareId]">
          <p class="highlight-requests__explain">
            It would show on their RM page as a Member Highlight, where anyone viewing {{ req.restaurant || 'the restaurant' }} on RM can see it
          </p>
          <div class="highlight-requests__actions">
            <VBtn
              :variant="suggested === 'yes' ? 'flat' : 'outlined'"
              color="primary"
              rounded="lg"
              :loading="saving === req.shareId"
              :disabled="!!saving"
              @click="answer(req, true)"
            >
              Yes, show it
            </VBtn>
            <VBtn
              :variant="suggested === 'no' ? 'flat' : 'outlined'"
              color="primary"
              rounded="lg"
              :disabled="!!saving"
              @click="answer(req, false)"
            >
              No, keep it with {{ req.restaurant || 'them' }}
            </VBtn>
          </div>
        </template>

        <p
          v-else
          class="highlight-requests__done"
        >
          <template v-if="answered[req.shareId] === 'yes'">
            It's on
            <RouterLink
              v-if="req.restaurantProfileId"
              :to="`/r/${req.restaurantProfileId}`"
            >
              {{ req.restaurant }}'s RM page
            </RouterLink>
            <template v-else>
              {{ req.restaurant }}'s RM page
            </template>
            now
          </template>
          <template v-else>
            Kept with {{ req.restaurant || 'the restaurant' }} only
          </template>
        </p>
      </section>

      <p
        v-if="!requests.length && !error"
        class="highlight-requests__empty"
      >
        Nothing is waiting for your answer.
        <RouterLink to="/favorites">
          Go to your Favorites
        </RouterLink>
      </p>

      <p
        v-if="error"
        class="highlight-requests__error"
      >
        {{ error }}
      </p>
    </template>
  </div>
</template>

<style scoped>
.highlight-requests {
  max-width: 560px;
  margin: 0 auto;
  padding: 24px 16px;
}

.highlight-requests__card + .highlight-requests__card {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--ds-border-subtle, rgba(var(--v-theme-on-surface), 0.08));
}

.highlight-requests__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ds-text-strong, rgb(var(--v-theme-on-surface)));
}

.highlight-requests__words {
  margin: 12px 0 0;
  font-size: 16px;
  line-height: 1.5;
  white-space: pre-line;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.highlight-requests__explain {
  margin: 12px 0 0;
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

/* Two answers, full width on a phone so each is an easy tap. */
.highlight-requests__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.highlight-requests__actions > * {
  flex: 1 1 200px;
}

.highlight-requests__done {
  margin: 16px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark, rgb(var(--v-theme-primary)));
}

.highlight-requests__empty {
  font-size: 16px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.highlight-requests__error {
  margin: 12px 0 0;
  font-size: 14px;
  color: rgb(var(--v-theme-error));
}

.highlight-requests__skeleton-line {
  height: 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.highlight-requests__skeleton-line.short {
  width: 60%;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
